import { Injectable } from '@angular/core';
import { DateChangeEvent } from '@progress/kendo-angular-scheduler';
import { BehaviorSubject, Subject, map, switchMap, tap, combineLatest, of, forkJoin } from 'rxjs';
import { IKendoSchedulerResource, ISchedulerEvent } from 'src/app/scheduler/models/scheduler';
import { RessourceService } from 'src/app/setting/services/ressource.service';
import { AbsenceGroupService } from '../services/absence-group.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RessourceType } from 'src/app/setting/models/ressource';
import { IBaseSearchRequest, IGridResult } from 'src/app/core/models/base';
import { AbsenceState, AbsenceType, IAbsenceGroup, IAbsencePlanning, ICreateAbsenceGroupApplicationRequest, ICreateAbsenceGroupRequest, ISearchAbsenceGroupsRequest } from '../models/absence-group';
import { ResourcePlanerService } from 'src/app/scheduler/services/resource-planer.service';
import { ITenantSettings } from 'src/app/setting/tenant-settings/models/tenant-setting';
import { TranslocoService } from '@jsverse/transloco';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { getCurrentEmployee } from 'src/app/stores/global/current-employee/current-employee.store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { Store } from '@ngrx/store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AbsenceGroupStoreService {

  constructor(
    private ressourceService: RessourceService,
    private absenceGroupService: AbsenceGroupService,
    private notificationService:  AppNotificationService,
    private translocoService: TranslocoService,
    private tenantSettings: TenantSettingsStoreService,
    private store: Store<AppStateInterface>,
    private permissionStore: PermissionStorageService,
    private resourcePlanerService: ResourcePlanerService
  ) { }

  private hasResourcesLoaded = false;

  private _absenceGroups$ = new BehaviorSubject<IGridResult<IAbsenceGroup[]>>({
    data: [],
    total: 0
  });
  public absenceGroups$ = this._absenceGroups$.asObservable();

  private _absenceEvents$ = new BehaviorSubject<ISchedulerEvent[]>([]);
  public absenceEvents$ = this._absenceEvents$.asObservable();

  private _resources$ = new BehaviorSubject<IKendoSchedulerResource[]>([
    {
      name: 'Ressources',
      data: [{
        text: "",
        id: "",
        color: "",
        department: "",
      }],
      field: 'employeeId',
      multiple: false,
      valueField: 'id',
      textField: 'text',
      colorField: 'color',
    },
  ]);
  public resources$ = this._resources$.asObservable();

  private loadSchedulerSwitchMap$ = new Subject<{from: Date, to: Date}>();

  get ressourceTypes(){
    return RessourceType
  }

  searchAbseceGroupRequest : ISearchAbsenceGroupsRequest = {
    pageNumber: 1,
    pageSize: 25,
    orderBy:['dateFrom desc']
  }

  currentSchedulerRange : {from: Date, to:Date};

  // Calendar events come from the planning endpoint, the same source as the main planner: every
  // employee's absences, reduced to "absent" (no type, no title) outside the viewer's reach by
  // the backend. Declined absences are not returned by it.
  schedulerSubscripton = this.loadSchedulerSwitchMap$
  .pipe(
    switchMap(({from, to}) =>
      this.resourcePlanerService.listAbsences({
        pageNumber: 1,
        pageSize: 999999,
        start: from,
        end: to
      })
    ),
    switchMap((result) => combineLatest([of(result.data), this.tenantSettings.tenantSettings$])),
    map(([absenceGroups, tenantSettings]) =>
      absenceGroups.map(absenceGroup => this.toSchedulerEvent(absenceGroup, tenantSettings))
    ),
    untilDestroyed(this)
  ).subscribe(
    schedulerEvents => this._absenceEvents$.next(schedulerEvents)
  )

  // One event per absence group, with the same half-day and redaction handling as scheduler-shared.
  private toSchedulerEvent(absenceGroup: IAbsencePlanning, tenantSettings: ITenantSettings): ISchedulerEvent {
    let startTime = new Date(absenceGroup.dateFrom);
    let endTime = new Date(
      new Date(absenceGroup.dateTo).setUTCHours(23, 59, 59, 999)
    );

    // absence ends in the morning of the last day
    if (absenceGroup.isHalfDayEnd) {
      const dayEndTimeSpanObj = TimeSpan.parse(tenantSettings.endMorningWorkTime).getAsTimeObject();
      endTime = new Date(
        new Date(absenceGroup.dateTo).setHours(dayEndTimeSpanObj.hours, dayEndTimeSpanObj.minutes)
      );
    }
    // absence starts in the afternoon of the first day
    if (absenceGroup.isHalfDayStart) {
      const dayStartTimeSpanObj = TimeSpan.parse(tenantSettings.startAfternoonWorkTime).getAsTimeObject();
      startTime = new Date(
        new Date(absenceGroup.dateFrom).setHours(dayStartTimeSpanObj.hours, dayStartTimeSpanObj.minutes)
      );
    }

    const typeLabel = absenceGroup.isRedacted
      ? this.translocoService.translate('absenceGroups.absent')
      : this.translocoService.translate('absenceGroups.absenceTypes.' + AbsenceType[absenceGroup.absenceType]);

    return {
      id: absenceGroup.id,
      start: startTime,
      end: endTime,
      title: absenceGroup.title ? `${typeLabel} - ${absenceGroup.title}` : typeLabel,
      employeeId: absenceGroup.employeeId,
      absenceState: absenceGroup.absenceState
    };
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }

  public initRessources(){
    if(!this.hasResourcesLoaded && this.hasPermission('Permissions.Ressources.Search')){
      this.loadRessources();
      this.hasResourcesLoaded = true;
    }
  }

  private loadRessources(){
    this.ressourceService
    .searchRessources({ pageNumber: 1, pageSize: 999999,ressourceType: this.ressourceTypes.Employee, isNotHidden: true })
    .pipe(
      untilDestroyed(this),
      map(result => result.data),
      map(data => {
        const ressourcesResources: IKendoSchedulerResource = {
          name: 'Ressources',
          data: [],
          field: 'employeeId',
          multiple: false,
          valueField: 'id',
          textField: 'text',
          colorField: 'color',
        };

        data.sort(function (ressource1, ressource2){
          if(ressource1.ressourceCategoryId > ressource2.ressourceCategoryId) return 1;
          if(ressource1.ressourceCategoryId < ressource2.ressourceCategoryId) return -1;
    
          if(ressource1.employee?.department.name > ressource2.employee?.department.name) return 1;
          if(ressource1.employee?.department.name < ressource2.employee?.department.name) return -1;
    
          if(ressource1.name > ressource2.name) return 1;
          if(ressource1.name < ressource2.name) return -1;
    
        });

        const ressources = data.map(ressource => {
          const ressourceResource = {
            text: `${ressource.name}`,
            id: ressource.employeeId,
            // Same fallback as the main planner (scheduler-shared): category colour, else department colour.
            color: ressource.ressourceCategory?.color ?? ressource.employee?.department?.schedulerColor,
            department: ressource.employee?.department?.name,
          };
          ressourcesResources.data.push(ressourceResource);
  
        })
          return ressourcesResources;
        })
    ).subscribe(
      resource => this._resources$.next([resource])
    )
  }

  loadScheudler(dateChange: DateChangeEvent){
    const from = dateChange.dateRange.start;
    const to = dateChange.dateRange.end;

    this.currentSchedulerRange = {from, to};
    this.loadSchedulerSwitchMap$.next({
      from,
      to
    });
  }

  // When true, the grid reloads via the approval endpoint (subordinates' open absences) instead of
  // the own/all search. Set once by the review component; the store is provided per component.
  forApproval = false;

  loadAbsenceGroups(){
    const search$ = this.forApproval
      ? this.absenceGroupService.searchAbsenceGroupsForApproval(this.searchAbseceGroupRequest)
      : this.absenceGroupService.searchAbsenceGroups(this.searchAbseceGroupRequest);

    search$
    .pipe(
      untilDestroyed(this),
      map(response => {
        let newResponse = response;
        newResponse.data = response.data.map(absenceGroup => {
          let difference = new Date(absenceGroup.dateTo).getTime() - new Date(absenceGroup.dateFrom).getTime();
          let totalDays = Math.ceil(difference / (1000 * 3600 * 24)) + 1;
          if(absenceGroup.isHalfDayStart){
            totalDays -= 0.5 
          }
          if(absenceGroup.isHalfDayEnd){
            totalDays -= 0.5 
          }
          return {
            ...absenceGroup,
            durationAsDays: totalDays
          }
        })

        return newResponse;
      }),
      tap(x => x))
    .subscribe(response => {
      this._absenceGroups$.next({
        data: response.data,
        total: response.totalCount
      })
    })
  }

  createAbsenceGroup(request: ICreateAbsenceGroupRequest){
    this.absenceGroupService.createAbsenceGroup(request)
    .pipe(untilDestroyed(this))
    .subscribe({
      next: (response) => {
        // reload AbsenceGroup for the Grid and AbsenceEvents for the Scheduler
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceSaveSuccess'), style:'success'})
        
      }
    })
  }

  updateAbsenceGroupState(id: string, newState: AbsenceState){
    this.absenceGroupService.updateAbsenceGroupState({absenceGroupId: id, absenceState: newState})
    .pipe(untilDestroyed(this))
    .subscribe({
      next: () =>{
        // reload AbsenceGroup for the Grid and AbsenceEvents for the Scheduler
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceSignedSuccess'), style:'success'});
        this.store.dispatch(getCurrentEmployee());
      }
    })
  }

  // Bulk-accept the selected absence groups (review view), then reload once.
  confirmAbsenceGroups(ids: string[]){
    if(!ids.length){
      return;
    }
    forkJoin(
      ids.map(id => this.absenceGroupService.updateAbsenceGroupState({absenceGroupId: id, absenceState: AbsenceState.Accepted}))
    )
    .pipe(untilDestroyed(this))
    .subscribe({
      next: () => {
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceSignedSuccess'), style:'success'});
        this.store.dispatch(getCurrentEmployee());
      }
    })
  }

  // Pull back an own, still open absence request. The backend removes it entirely - unlike an
  // expense there is no draft state to fall back to.
  withdrawAbsenceGroup(id: string){
    this.absenceGroupService.withdrawAbsenceGroup(id)
    .pipe(untilDestroyed(this))
    .subscribe({
      next: () => {
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceWithdrawnSuccess'), style:'success'});
        this.store.dispatch(getCurrentEmployee());
      },
      // A supervisor may have decided in the meantime; the error interceptor shows the conflict.
      // Reload either way so the row reflects its real current state.
      error: () => this.loadAbsenceGroups()
    })
  }

  deleteAbsenceGroup(id: string){
    this.absenceGroupService.deleteAbsenceGroup(id)
    .pipe(untilDestroyed(this))
    .subscribe({
      next: () =>{
        // reload AbsenceGroup for the Grid and AbsenceEvents for the Scheduler
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceDeleteSuccess'), style:'success'});
        this.store.dispatch(getCurrentEmployee());
      }
    })
  }

  applyAbsenceGroup(request: ICreateAbsenceGroupApplicationRequest){
    this.absenceGroupService.applyAbsenceGroup(request)
    .pipe(untilDestroyed(this))
    .subscribe({
      next: (response) => {
        this.loadAbsenceGroups();
        if(this.currentSchedulerRange){
          this.loadSchedulerSwitchMap$.next(this.currentSchedulerRange);
        }
        this.notificationService.showNotification({message: this.translocoService.translate('absenceGroups.absenceSaveSuccess'), style:'success'});
        this.store.dispatch(getCurrentEmployee());
      }
      
    })
  }

}
